import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, LogOut, ExternalLink } from "lucide-react";
import { api, projectPoster } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ProjectForm } from "./ProjectForm";
import { SettingsManager } from "./SettingsManager";

const ProjectsManager = () => {
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null); // project obj or "new" or null

  const load = async () => {
    const { data } = await api.get("/projects");
    setProjects(data);
  };
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await api.delete(`/projects/${p.id}`);
    toast.success("Project deleted");
    load();
  };

  const move = async (index, dir) => {
    const next = [...projects];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setProjects(next);
    await api.post("/projects/reorder", { ids: next.map((p) => p.id) });
  };

  const onSaved = () => { setEditing(null); load(); };

  if (editing) {
    return <ProjectForm initial={editing === "new" ? null : editing} onSaved={onSaved} onCancel={() => setEditing(null)} />;
  }

  return (
    <div data-testid="projects-manager">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{projects.length} project(s) · reorder with the arrows</p>
        <button onClick={() => setEditing("new")} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium text-black hover:opacity-90" style={{ background: "var(--accent-color)" }} data-testid="add-project-btn">
          <Plus size={16} /> Add Project
        </button>
      </div>

      <div className="space-y-3">
        {projects.map((p, i) => (
          <div key={p.id} className="flex items-center gap-4 rounded-xl border p-3" style={{ borderColor: "var(--border-color)", background: "var(--surface-color)" }} data-testid={`admin-project-${i}`}>
            <div className="flex flex-col gap-1">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 disabled:opacity-30 hover:text-[var(--accent-color)]" data-testid={`move-up-${i}`}><ArrowUp size={16} /></button>
              <button onClick={() => move(i, 1)} disabled={i === projects.length - 1} className="p-1 disabled:opacity-30 hover:text-[var(--accent-color)]" data-testid={`move-down-${i}`}><ArrowDown size={16} /></button>
            </div>
            <div className="h-16 w-24 shrink-0 overflow-hidden rounded-lg border" style={{ borderColor: "var(--border-color)" }}>
              {projectPoster(p).src ? <img src={projectPoster(p).src} alt="" className="h-full w-full object-cover" /> : null}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-heading font-medium">{p.title}</p>
              <p className="truncate text-xs" style={{ color: "var(--text-secondary)" }}>
                <span style={{ color: "var(--accent-color)" }}>{p.format === "long" ? "Long form" : "Short form"}</span>
                {p.video_url ? " · has video" : " · empty"}
              </p>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setEditing(p)} className="p-2 rounded-lg hover:text-[var(--accent-color)]" data-testid={`edit-project-${i}`}><Pencil size={16} /></button>
              <button onClick={() => remove(p)} className="p-2 rounded-lg hover:text-red-400" data-testid={`delete-project-${i}`}><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("projects");

  const out = () => { logout(); nav("/admin/login"); };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-color)" }} data-testid="admin-dashboard">
      <header className="border-b" style={{ borderColor: "var(--border-color)" }}>
        <div className="mx-auto max-w-5xl px-6 h-16 flex items-center justify-between">
          <span className="font-heading font-semibold tracking-tight">Studio Admin</span>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm hover:text-[var(--accent-color)]" style={{ color: "var(--text-secondary)" }} data-testid="view-site-link">
              View site <ExternalLink size={14} />
            </a>
            <button onClick={out} className="inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm hover:border-[var(--accent-color)] transition-colors" style={{ borderColor: "var(--border-color)" }} data-testid="logout-btn">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-6 py-10">
        <div className="mb-8 flex gap-2 border-b" style={{ borderColor: "var(--border-color)" }}>
          {["projects", "settings"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className="relative px-4 py-3 text-sm capitalize transition-colors"
              style={{ color: tab === t ? "var(--text-primary)" : "var(--text-secondary)" }}
              data-testid={`tab-${t}`}
            >
              {t}
              {tab === t && <span className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: "var(--accent-color)" }} />}
            </button>
          ))}
        </div>

        {tab === "projects" ? <ProjectsManager /> : <SettingsManager />}
      </div>
    </div>
  );
}
